import cx from 'classnames';
import {
  COLLECTION_MODEL_TYPE__MODEL_TYPE__MAP,
  MODEL_TYPES,
} from '../constants/constants';
import { useUserContext } from '../contexts/UserContext';
import {
  getCollectionRenderPath,
  getCollectionTemplatePath,
  getConfigruatorPath,
  getRetailAutomationPath,
  getSharedFilesRenderPath,
  getSharedFilesTemplatePath,
} from '../helpers/urlHelper';
import { ThumbnailCardAutomation } from './ThumbnailCardAutomation';
import { ThumbnailCardCollection } from './ThumbnailCardCollection';
import { ThumbnailCardConfigurator } from './ThumbnailCardConfigurator';
import { ThumbnailCardConfigurators } from './ThumbnailCardConfigurators';
import { ThumbnailCardModel3D, ThumbnailCardProduct } from './ThumbnailCardProduct';
import { ThumbnailCardProject } from './ThumbnailCardProject';
import { ThumbnailCardRenderPreviews } from './ThumbnailCardRenderPreviews';
import { ThumbnailCardRoom, ThumbnailCardTemplate } from './ThumbnailCardTemplate';
import { ThumbnailCardTexture, ThumbnailCardUpload } from './ThumbnailCardTexture';
import styles from './ThumbnailContainerV2.module.scss';
import { ThumbnailCardLightroom } from './ThumbnailCardLightroom';
import { ThumbnailCardRetailAutomation } from './ThumbnailCardRetailAutomation';
import { ThumbnailCardImagineAI } from './ThumbnailCardImagineAI';
import { ThumbnailCardProductBuilder } from './ThumbnailCardProductBuilder';
import { ThumbnailCardSharedProductPage } from './ThumbnailCardSharedProduct';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import { userPilotEvents } from './Userpilot/userpilotHelper';
import { selectCopyInfo } from '../redux/slicers/appSlicer';
import { dispatcher } from '../helpers/projectHelper';
import { useSelector } from 'react-redux';
import { updateBulkRenderImageConfig } from '../helpers/api';
import { getCopyPasteActions } from '../helpers/imageEditsHelper';

export const CARD_COMPONENT_MAP = {
  [MODEL_TYPES.PROJECTS]: ThumbnailCardProject,
  [MODEL_TYPES.TEMPLATES]: ThumbnailCardTemplate,
  [MODEL_TYPES.ROOM]: ThumbnailCardRoom,
  [MODEL_TYPES.PRODUCT]: ThumbnailCardProduct,
  [MODEL_TYPES.THREEDMODELS]: ThumbnailCardModel3D,
  [MODEL_TYPES.TEXTURE]: ThumbnailCardTexture,
  [MODEL_TYPES.UPLOADS]: ThumbnailCardUpload,
  [MODEL_TYPES.COLLECTION]: ThumbnailCardCollection,
  [MODEL_TYPES.PREVIEWS]: ThumbnailCardRenderPreviews,
  [MODEL_TYPES.PROJECTRENDER]: ThumbnailCardRenderPreviews,
  [MODEL_TYPES.AUTOMATION]: ThumbnailCardAutomation,
  [MODEL_TYPES.CONFIGURATOR]: ThumbnailCardConfigurator,
  [MODEL_TYPES.CONFIGURATORS]: ThumbnailCardConfigurators,
  [MODEL_TYPES.PRODUCT_CONFIGURATOR]: ThumbnailCardConfigurator,
  [MODEL_TYPES.LIGHTROOM]: ThumbnailCardLightroom,
  [MODEL_TYPES.RETAIL_AUTOMATION]: ThumbnailCardRetailAutomation,
  [MODEL_TYPES.IMAGINE_AI]: ThumbnailCardImagineAI,
  [MODEL_TYPES.PRODUCT_BUILDER]: ThumbnailCardProductBuilder,
  [MODEL_TYPES.SHARED_PRODUCT_PAGE]: ThumbnailCardSharedProductPage,
};

export const ThumbnailContainerV2 = ({
  className = '',
  items,
  modelType,
  selected = [],
  cardProps: {
    resetSelection = () => {},
    refetchList,
    fullWidth,
    onSelect,
    onEdit,
    onEditInfo,
    hideFavourite,
    customActions,
    onDelete,
    onClick,
    curatorProp,
    to,
    onDuplicatePass,
    renderPreviews = false,
    isCollectionItem = false, // for collection model types different from all other model types :( -> we need to map them to normal model types
  } = {},
  onlyItems,
  Component,
  onLinkClick,
  templateFilter,
  isTemplateCard = false,
  contextMenu,
  isSharedItem = false, // to get to know that i'm working on shared item card,
  currentMenuId,
  setCurrentMenu,
  designV2,
  isSharedPage = false,
  showMenu = true,
  redirect = true,
  refetchData,
  showDownload = false,
  ...rest
}) => {
  // if (!Component) throw new Error('Card type is not supoprted');
  const { trackClickEvent } = userPilotEvents();
  const [activeIndexByItem, setActiveIndexByItem] = useState({});
  const { canAccessLiveOnAppData } = useUserContext();
  const location = useLocation();
  const copyInfo = useSelector(selectCopyInfo);
  const showDownloadButton = location.pathname === '/my-products/textures' || showDownload;

  let _customActions = { ...(customActions || {}) };

  const renderedList = items.map((item) => {
    const _customActionsForItem = { ..._customActions };
    const itemKey = item?.id ?? item?.data?.id;
    const itemActiveIndex = activeIndexByItem[itemKey] || 0;

    const itemImages = item?.images || item?.data?.images || [];
    const currentImage = itemImages[itemActiveIndex];
    const activeCameraId = currentImage?.id;
    
    let itemModelType;
    if (isCollectionItem) {
      itemModelType = COLLECTION_MODEL_TYPE__MODEL_TYPE__MAP[item.model_type];
      const collectionId = item.collection.id;
      const routes = {
        [MODEL_TYPES.PREVIEWS]: getCollectionRenderPath(collectionId, item.data.id),
        [MODEL_TYPES.PROJECTRENDER]: getCollectionRenderPath(collectionId, item.data.id),
        [MODEL_TYPES.TEMPLATES]: getCollectionTemplatePath(collectionId, item.data.id),
        [MODEL_TYPES.ROOM]: getCollectionTemplatePath(collectionId, item.data.id),
        [MODEL_TYPES.PRODUCT_CONFIGURATOR]: getConfigruatorPath(item.data.id),
        [MODEL_TYPES.RETAIL_AUTOMATION]: getRetailAutomationPath(item.data.id),
      };
      to = routes[itemModelType];
      // Commented this as we need download in `Custom Maps` also
      // if ([MODULE_TYPE_OPTIONS.TEXTURES].includes(parseInt(item?.model_type))) {
      //   // Disable downloading textures from the 3-dot menu options
      //   _customActions = { ..._customActions, [ACTION.DOWNLOAD]: null };
      // }
      Object.assign(
        _customActionsForItem,
        getCopyPasteActions({
          copyInfo,
          activeCameraId,
          currentImage,
          dispatcher,
          designV2,
          itemImages,
          updateBulkRenderImageConfig,
          refetchList,
          trackClickEvent,
          rest,
        })
      );
    } else if (isSharedItem) {
      itemModelType = item.model_type;
      const routes = {
        [MODEL_TYPES.PREVIEWS]: getSharedFilesRenderPath(item.data.id),
        [MODEL_TYPES.PROJECTRENDER]: getSharedFilesRenderPath(item.data.id),
        [MODEL_TYPES.TEMPLATES]: getSharedFilesTemplatePath(item.data.id),
        [MODEL_TYPES.ROOM]: getSharedFilesTemplatePath(item.data.id),
      };
      to = routes[itemModelType];
      // Enable Copy Edits & Paste & Apply for shared items with render previews
      if ([MODEL_TYPES.PREVIEWS, MODEL_TYPES.PROJECTRENDER].includes(itemModelType)) {
        Object.assign(
          _customActionsForItem,
          getCopyPasteActions({
            copyInfo,
            activeCameraId,
            currentImage,
            dispatcher,
            designV2,
            itemImages,
            updateBulkRenderImageConfig,
            refetchList,
            trackClickEvent,
            rest,
          })
        );
      }
    } else if (renderPreviews) {
      itemModelType = MODEL_TYPES.PROJECTRENDER;
      Object.assign(
        _customActionsForItem,
        getCopyPasteActions({
          copyInfo,
          activeCameraId,
          currentImage,
          dispatcher,
          designV2,
          itemImages,
          updateBulkRenderImageConfig,
          refetchList,
          trackClickEvent,
          rest,
        })
      );
      
    } else {
      itemModelType = modelType || item.model_type; // // model_type will be if we have shared files
    }

    const isMaterialList = itemModelType === 22;

    let ItemComponent = Component || CARD_COMPONENT_MAP[itemModelType === 22 ? MODEL_TYPES.TEXTURE: itemModelType];

    if (!ItemComponent) return null;

    const data = item.model_type ? item.data : item;
    const key = item.model_type ? item.id : data.id;
    let additionalData;
    if (item.data) {
      additionalData = {
        date:
          isCollectionItem || isSharedItem
            ? (COLLECTION_MODEL_TYPE__MODEL_TYPE__MAP[item.model_type] === MODEL_TYPES.TEMPLATES ||
                COLLECTION_MODEL_TYPE__MODEL_TYPE__MAP[item.model_type] === MODEL_TYPES.ROOM) &&
              canAccessLiveOnAppData() &&
              item?.data?.is_custom
              ? item?.data.updated_at
              : undefined
            : item.updated_at,
        secondLine:
          isCollectionItem || isSharedItem
            ? canAccessLiveOnAppData() && item?.data?.is_custom
              ? ''
              : item?.data?.owner_organization_details?.name
            : '',
      };
    }

    const props = {};
    if (to) {
      props.to = to;
    }
    if (refetchList) {
      props.refetchList = refetchList;
    }
    if (onLinkClick) {
      props.onLinkClick = onLinkClick;
    }
    if (isTemplateCard) {
      props.isTemplateCard = isTemplateCard;
    }
    if (contextMenu) {
      props.contextMenu = contextMenu;
    }

    if (!showMenu && !redirect) {
      onClick = (val) => onSelect(val?.id);
    }
    let isTexture = 'is_special_case_texture' in (data ?? {});
    if (!isTexture) {
      isTexture = Boolean(data?.texture_file || data?.low_texture_file);
    }
    const isModel = !isTexture && (data?.blender_id || data?.is_prop_group || data?.product_file);

    return (
      // <></>
      <ItemComponent
        key={key}
        data={data}
        setActiveIndex={(index) => {
          if (itemKey == null) return;
          setActiveIndexByItem((prev) => ({ ...prev, [itemKey]: index }));
        }}
        activeIndex={itemActiveIndex}
        originalData={item}
        itemModelType={itemModelType}
        sharePageColletionCard={+itemModelType === +MODEL_TYPES.COLLECTION}
        additionalData={additionalData}
        selected={selected.includes(item.id) || selected.includes(item?.data?.id)}
        isAnySelected={selected?.length ? true : false}
        fullWidth={fullWidth}
        onSelect={onSelect}
        onEdit={onEdit}
        // onEditInfo={onEditInfo}
        hideFavourite={hideFavourite}
        customActions={_customActionsForItem}
        onDelete={onDelete}
        onClick={onClick}
        resetSelection={resetSelection}
        isCollectionItem={isCollectionItem}
        isSharedItem={isSharedItem}
        curatorProp={curatorProp}
        templateFilter={templateFilter}
        currentMenuId={currentMenuId}
        setCurrentMenu={(val) => setCurrentMenu(val)}
        designV2={designV2}
        isSharedPage={isSharedPage}
        onDuplicatePass={onDuplicatePass}
        showMenu={showMenu}
        redirect={redirect}
        refetchData={refetchData}
        isMaterialList={isMaterialList || rest.isMaterialList}
        isTexture={showDownloadButton || item?.model_type === MODEL_TYPES.TEXTURE}
        {...props}
        {...rest}
        {...(rest?.canAccessV2Page
          ? {
              onGenerateContent:
                isModel || isTexture ? () => rest?.onGenerateContent?.(data) : null,
            }
          : {})}
        // {...(rest?.canAccessV2Page
        //   ? {
        //     onGenerateDimension:
        //         isModel
        //           ? () => onGenDimenLocal(data)
        //           : null,
        //     }
        // : {})}
      />
    );
  });

  if (onlyItems) {
    return renderedList;
  }

  return (
    <div className={cx(!showMenu && !redirect ? styles.popupGrid : styles.grid, className)}>
      {renderedList}
    </div>
  );
};
